import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Modal, Grid } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CloseIcon from "@mui/icons-material/Close";
import { CustomButton } from "../ReusableComponents/CustomButton";
import theme from "../../theme";

/* Constants */
const CARD_PADDING = 4;
const BORDER_RADIUS_MEDIUM = theme.borderRadius.medium;
const BORDER_RADIUS_LARGE = theme.borderRadius.large;
const BORDER_RADIUS_XL = theme.borderRadius.xl;
const BORDER_RADIUS_XXL = theme.borderRadius.xxl;
const BORDER_RADIUS_50 = theme.borderRadius.border50;
const ICON_BACKGROUND_SIZE = 70;
const ICON_SIZE = 40;
const HEADING_SPACING = 1.5;
const CONTENT_SPACING = 3;
const FEATURE_SPACING = 2;
const BUTTON_SPACING = 2;
const MODAL_PADDING = { xs: 3, md: 5 };
const MODAL_ICON_SIZE = 64;
const SUBMODULE_PADDING = 3;
const SUBMODULE_ICON_SIZE = 48;

export const ModuleCard = ({
  module,
  accountInfo,
  shouldReserveSpaceForBadge,
}) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [hoveredOption, setHoveredOption] = useState(null);

  const {
    title,
    name,
    description,
    icon: Icon,
    gradient,
    accentColor,
    features = [],
    route,
    path,
    hasSubModules,
    getSubModules,
  } = module || {};

  const displayTitle = title || name || module?.label || module?.name;

  // If the account has sub-modules and getSubModules is a function, call it with accountInfo. Otherwise, default to an empty array.
  const subModules =
    hasSubModules && typeof getSubModules === "function"
      ? getSubModules(accountInfo)
      : [];

  const hasMultipleTools = module?.id === "ai-toolkit" && subModules.length > 1;
  const hasSingleTool = module?.id === "ai-toolkit" && subModules.length === 1;

  const handleClick = () => {
    try {
      if (hasSubModules && subModules.length > 0) {
        // If the user only has one submodule natigate directly
        if (subModules.length === 1) {
          navigate(subModules[0].path);
        } else {
          setShowOptionsModal(true);
        }
      } else {
        // No sub-modules: navigate to direct route
        const destination = route || path || module?.route || module?.path;
        if (destination) {
          navigate(destination);
        } else {
          console.warn("No route defined for module:", displayTitle);
        }
      }
    } catch (error) {
      console.error("Error navigating:", error);
    }
  };

  const handleOptionClick = (optionPath) => {
    try {
      setShowOptionsModal(false);
      navigate(optionPath);
    } catch (error) {
      console.error("Error navigating to tool:", error);
    }
  };

  const handleCloseModal = () => {
    setShowOptionsModal(false);
    setHoveredOption(null);
  };

  // Filter features based on accessible sub-modules
  const getFilteredFeatures = () => {
    if (!hasSubModules || subModules.length === 0) {
      return features.map((f) => (typeof f === "string" ? f : f.text));
    }

    // For AI toolkit card, only show features available to the client
    const availableSubModuleNames = subModules.map((sub) => sub.name);
    return features
      .filter((f) => {
        if (typeof f === "string") return true;
        return availableSubModuleNames.some((name) =>
          name.includes(f.requiresSubModule)
        );
      })
      .map((f) => (typeof f === "string" ? f : f.text));
  };

  const filteredFeatures = getFilteredFeatures();

  return (
    <>
      {/* Main Card */}
      <Box
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          position: "relative",
          cursor: "pointer",
          height: "100%",
          minHeight: { xs: "auto", md: "500px" },
        }}
      >
        {/* Animated glow effect */}
        <Box
          sx={{
            position: "absolute",
            inset: "-4px",
            borderRadius: BORDER_RADIUS_XXL,
            // background: gradient, // Makes like a shadow, look into when applied background
            opacity: isHovered ? 1 : 0,
            filter: "blur(16px)",
            transition: "opacity 0.7s ease",
            animation: isHovered ? "float 3s ease-in-out infinite" : "none",
          }}
        />

        {/* Card Content */}
        <Box
          sx={{
            position: "relative",
            backgroundColor: "primary.main",
            borderRadius: BORDER_RADIUS_XXL,
            p: CARD_PADDING,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            border: `2px solid ${accentColor}`,
            // boxShadow: isHovered ? `0 20px 60px ${accentColor}40` : "none",
            transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: isHovered ? "translateY(-8px)" : "translateY(0)",
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", md: "column" },
              alignItems: "flex-start",
              gap: { xs: 3, md: 0 },
              mb: CONTENT_SPACING,
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: ICON_BACKGROUND_SIZE,
                height: ICON_BACKGROUND_SIZE,
                borderRadius: BORDER_RADIUS_LARGE,
                background: gradient,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: { xs: 0, md: CONTENT_SPACING },
                flexShrink: 0,
              }}
            >
              {Icon && (
                <Icon sx={{ color: "primary.white", fontSize: ICON_SIZE }} />
              )}
            </Box>

            {/* Title & Description */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  mb: HEADING_SPACING,
                  color: "primary.white",
                  fontSize: { xs: "20px", md: "24px" },
                }}
              >
                {displayTitle}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "primary.white",
                  fontSize: { xs: "14px", md: "16px" },
                }}
              >
                {description}
              </Typography>
            </Box>
          </Box>

          {/* Features List */}
          <Box
            sx={{
              flexGrow: 1,
              mb: BUTTON_SPACING,
            }}
          >
            <Box
              sx={{
                columnCount: { xs: 1, sm: 2, md: 1 },
                columnsGap: 3,
              }}
            >
              {filteredFeatures.map((feature, idx) => (
                <Box
                  key={idx}
                  sx={{
                    breakInside: "avoid", // Keeps the bullet point and the text together
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    mb: FEATURE_SPACING,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: BORDER_RADIUS_50,
                      backgroundColor: accentColor,
                      marginTop: "6px",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "primary.white",
                      lineHeight: 1.5,
                      fontSize: "14px",
                    }}
                  >
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Single Tool Description - Only for AI Toolkit with 1 tool */}
            {hasSingleTool && (
              <Box
                sx={{
                  mt: { sm: 2, md: 0 },
                  "& .MuiTypography-root": {
                    color: "primary.white",
                    fontSize: "14px",
                    lineHeight: 1.5,
                  },
                }}
              >
                {subModules[0].popoverContent}
              </Box>
            )}
          </Box>

          {/* Button */}
          <Box
            sx={{
              display: "block",
              display: {
                xs: "block",
                sm: shouldReserveSpaceForBadge ? "grid" : "block",
                md: shouldReserveSpaceForBadge ? "grid" : "block",
              },
              gap: { sm: shouldReserveSpaceForBadge ? 2 : 0 },
              // Apply desktop-only styles -> Use grid for all cards with sub-modules to keep button alignment consistent across the dashboard
              [theme.breakpoints.up("md")]: {
                gridTemplateRows: shouldReserveSpaceForBadge
                  ? "max-content 30px"
                  : undefined,
                gap: shouldReserveSpaceForBadge ? 2 : 0,
                alignItems: "center",
              },
            }}
          >
            <CustomButton
              text={hasMultipleTools ? "Select Tools" : "Go to Module"}
              onClick={handleClick}
              fullWidth
              endIcon={<NavigateNextIcon />}
              iconColor="primary.white"
              color="primary.white"
              backgroundColor="transparent"
              borderRadius={BORDER_RADIUS_MEDIUM}
              sx={{
                background: gradient,
                fontWeight: 600,
                fontSize: "14px",
                px: 2,
                //   boxShadow: isHovered ? `0 10px 30px ${accentColor}60` : "none", // Look into when applied background
                transition: "all 0.5s ease",
                transform: isHovered ? "scale(1.05)" : "scale(1)",
                "&:hover": {
                  background: gradient,
                },
                "& .MuiButton-endIcon": {
                  transition: "transform 0.3s ease",
                  transform: isHovered ? "translateX(8px)" : "translateX(0)",
                },
              }}
            />

            {/* Tools */}
            {hasMultipleTools && (
              <Box
                sx={{
                  textAlign: "center",
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    display: "inline-block",
                    backgroundColor: `${accentColor}20`,
                    color: accentColor,
                    borderRadius: BORDER_RADIUS_XL,
                    padding: "6px 16px",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  {subModules.length} tools available
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Options Modal - Only for modules with multiple sub-modules */}
      {hasSubModules && subModules.length > 1 && (
        <Modal
          open={showOptionsModal}
          onClose={handleCloseModal}
          aria-label="options-modal-title"
          slotProps={{
            backdrop: {
              sx: {
                backgroundColor: "rgba(0,0,0,0.7)",
                backdropFilter: "blur(5px)",
              },
            },
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: "primary.main",
              borderRadius: BORDER_RADIUS_XXL,
              p: MODAL_PADDING,
              maxWidth: "900px",
              width: "90%",
              maxHeight: "90vh",
              overflow: "auto",
              border: `2px solid ${accentColor}60`,
              boxShadow: `0 25px 100px ${accentColor}40`,
              outline: "none",
              position: "relative",
            }}
          >
            {/* Close Button */}
            <Box
              onClick={handleCloseModal}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                cursor: "pointer",
                width: ICON_SIZE,
                height: ICON_SIZE,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  transform: "rotate(90deg)",
                },
              }}
            >
              <CloseIcon sx={{ color: "primary.white" }} />
            </Box>

            {/* Modal Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                mb: 5,
              }}
            >
              <Box
                sx={{
                  width: MODAL_ICON_SIZE,
                  height: MODAL_ICON_SIZE,
                  borderRadius: BORDER_RADIUS_LARGE,
                  background: gradient,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {Icon && <Icon sx={{ fontSize: 32, color: "primary.white" }} />}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    color: "primary.white",
                    fontSize: { xs: "24px", md: "32px" },
                  }}
                >
                  {displayTitle}
                </Typography>
                <Typography variant="body1" sx={{ color: "primary.white" }}>
                  Select a tool to get started
                </Typography>
              </Box>
            </Box>

            {/* Sub-modules Grid */}
            <Grid container spacing={3}>
              {subModules.map((subModule, idx) => {
                const SubIcon = subModule.icon;

                return (
                  <Grid size={{ xs: 12, sm: 6 }} key={subModule.path || idx}>
                    <Box
                      onMouseEnter={() => setHoveredOption(idx)}
                      onMouseLeave={() => setHoveredOption(null)}
                      onClick={() => handleOptionClick(subModule.path)}
                      sx={{
                        padding: SUBMODULE_PADDING,
                        borderRadius: BORDER_RADIUS_LARGE,
                        backgroundColor:
                          hoveredOption === idx
                            ? `${accentColor}10`
                            : "rgba(255, 255, 255, 0.02)",
                        border: `2px solid ${
                          hoveredOption === idx
                            ? accentColor
                            : "rgba(255, 255, 255, 0.1)"
                        }`,
                        cursor: "pointer",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.5s ease",
                        transform:
                          hoveredOption === idx
                            ? "translateX(8px) scale(1.02)"
                            : "translateX(0) scale(1)",
                        boxShadow:
                          hoveredOption === idx
                            ? `0 10px 40px ${accentColor}30`
                            : "none",
                      }}
                    >
                      {/* Sub-module Header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        {SubIcon && (
                          <Box
                            sx={{
                              width: SUBMODULE_ICON_SIZE,
                              height: SUBMODULE_ICON_SIZE,
                              borderRadius: BORDER_RADIUS_MEDIUM,
                              background:
                                hoveredOption === idx
                                  ? gradient
                                  : `${accentColor}20`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              transition: "all 0.3s ease",
                            }}
                          >
                            <SubIcon
                              sx={{
                                fontSize: 24,
                                color:
                                  hoveredOption === idx
                                    ? " primary.white"
                                    : accentColor,
                              }}
                            />
                          </Box>
                        )}

                        {/* Sub-module Name */}
                        <Typography variant="subtitle2" sx={{ color: "primary.white" }}>
                          {subModule.name}
                        </Typography>
                      </Box>

                      {/* Sub-module Description */}
                      {subModule.popoverContent && (
                        <Box
                          sx={{
                            mt: { sm: 2, md: 0 },
                            "& .MuiTypography-root": {
                              color: "primary.white",
                              fontSize: "14px",
                              lineHeight: 1.5,
                            },
                          }}
                        >
                          {subModule.popoverContent}
                        </Box>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Modal>
      )}
    </>
  );
};
